import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight, MoreHorizontal, Users } from 'lucide-react';
import type { Member } from '@/types';
import type { TableColumn, ColumnSort } from './TableColumns';

interface MemberTableProps {
  members: Member[];
  columns: TableColumn[];
  sort: ColumnSort | null;
  onSort: (field: 'id' | 'name' | 'email' | 'phone' | 'joinedDate' | 'endDate') => void;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export function MemberTable({
  members,
  columns,
  sort,
  onSort,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: MemberTableProps) {
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handleSort = (field: 'id' | 'name' | 'email' | 'phone' | 'joinedDate' | 'endDate') => {
    onSort(field);
  };

  if (members.length === 0) {
    return (
      <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <Users className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-700">No members found</p>
          <p className="mt-1 text-sm text-gray-500">Add a new member to get started, or adjust your search.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-hide">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80">
            <TableRow className="border-gray-200 hover:bg-transparent">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={cn(column.className, 'h-11 px-4 text-left align-middle')}
                >
                  {column.renderHeader(sort, handleSort)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow
                key={member.id}
                className="border-gray-100 transition-colors hover:bg-gray-50/80"
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.className, 'px-4 py-3 align-middle')}
                  >
                    {column.renderCell(member)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-gray-200 bg-white px-4 py-2.5">
        <p className="text-sm text-gray-500">
          Showing{' '}
          <span className="font-medium text-gray-700">
            {startIndex + 1}–{endIndex}
          </span>{' '}
          of <span className="font-medium text-gray-700">{totalItems}</span>
        </p>

        <div className="flex items-center gap-3">
          {onPageSizeChange && (
            <div className="flex items-center gap-2">
              <label htmlFor="member-table-page-size" className="text-sm text-gray-500">
                Per page
              </label>
              <div className="relative">
                <select
                  id="member-table-page-size"
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="h-8 cursor-pointer appearance-none rounded-md border border-gray-200 bg-white py-0 pl-2.5 pr-7 text-sm text-gray-700 outline-none focus:border-gray-300 focus:outline-none focus:ring-0"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {getPageNumbers().map((page, index) =>
                page === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="flex h-8 w-8 items-center justify-center text-gray-400"
                    aria-hidden
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </span>
                ) : (
                  <Button
                    key={page}
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                      'h-8 min-w-8 px-2.5 text-sm',
                      currentPage === page
                        ? 'border-[#4369a0]/30 bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    )}
                    onClick={() => onPageChange(page)}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
